import type { Components } from '@/api/generated/api';
import { ObservationTimelineEntryType } from '@/api/generated/api.enum';
type TTimelineRowIdentity = {
  type: Components.Schemas.ObservationTimelineEntryType;
  note: Pick<Components.Schemas.NoteDto, 'id' | 'createdAt' | 'date'> | null;
  performanceAssessmentEntry: Pick<Components.Schemas.TimelineAssessmentSummaryDto, 'id' | 'createdAt' | 'date'> | null;
  supportMeasureEntry: Pick<Components.Schemas.SupportMeasureTimelineEntryDto, 'id' | 'createdAt'> | null;
};

interface ITimelineRowOrder {
  sourceCreatedAt: string;
  sourceId: number;
  timelineDate: string;
}

const toTimestamp = (value: string): number => {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getTimelineRowOrder = (row: TTimelineRowIdentity): ITimelineRowOrder => {
  if (row.type === ObservationTimelineEntryType.note && row.note) {
    return { sourceCreatedAt: row.note.createdAt, sourceId: row.note.id, timelineDate: row.note.date };
  }
  if (row.type === ObservationTimelineEntryType.supportMeasureEntry && row.supportMeasureEntry) {
    return {
      sourceCreatedAt: row.supportMeasureEntry.createdAt,
      sourceId: row.supportMeasureEntry.id,
      timelineDate: row.supportMeasureEntry.createdAt,
    };
  }
  if (row.performanceAssessmentEntry) {
    return {
      sourceCreatedAt: row.performanceAssessmentEntry.createdAt,
      sourceId: row.performanceAssessmentEntry.id,
      timelineDate: row.performanceAssessmentEntry.date,
    };
  }
  return { sourceCreatedAt: '', sourceId: 0, timelineDate: '' };
};

/**
 * Mirrors the server's descending timelineDate/sourceCreatedAt order. The API does not expose the
 * projection ID, so the source ID is the deterministic final fallback when ordering grouped timeline cards.
 */
export const compareTimelineRows = (left: TTimelineRowIdentity, right: TTimelineRowIdentity): number => {
  const leftOrder = getTimelineRowOrder(left);
  const rightOrder = getTimelineRowOrder(right);
  return (
    toTimestamp(rightOrder.timelineDate) - toTimestamp(leftOrder.timelineDate) ||
    toTimestamp(rightOrder.sourceCreatedAt) - toTimestamp(leftOrder.sourceCreatedAt) ||
    rightOrder.sourceId - leftOrder.sourceId
  );
};

export const getTimelineEntryKey = (
  type: Components.Schemas.ObservationTimelineEntryType,
  id: number | undefined,
): string => {
  switch (type) {
    case ObservationTimelineEntryType.note:
      return `note-${id}`;
    case ObservationTimelineEntryType.performanceAssessmentEntry:
      return `entry-${id}`;
    case ObservationTimelineEntryType.supportMeasureEntry:
      return `support-measure-${id}`;
  }
};

export const getTimelineRowKey = (row: TTimelineRowIdentity): string => {
  switch (row.type) {
    case ObservationTimelineEntryType.note:
      return getTimelineEntryKey(row.type, row.note?.id);
    case ObservationTimelineEntryType.performanceAssessmentEntry:
      return getTimelineEntryKey(row.type, row.performanceAssessmentEntry?.id);
    case ObservationTimelineEntryType.supportMeasureEntry:
      return getTimelineEntryKey(row.type, row.supportMeasureEntry?.id);
  }
};
